/**
 * Per-city timezone resolution for the web app.
 *
 * Server-authoritative: home-feed surfaces format event timestamps against the
 * city's zone, never the runtime clock (which is UTC on Vercel).
 * `greetingContext.ts` (server-only), the day-grouping helpers (pure,
 * unit-testable), and the home-feed rails / Top Match hero (WEB-409) all read
 * from here so they can never disagree on which zone a city's day boundaries
 * fall on.
 *
 * **The zones are not defined here (API-1704).** A local `CITY_TIMEZONES` map
 * used to own them, and its docblock called itself the single source of truth
 * for per-city timezone resolution — while `@loci/validation` and the venue
 * routes each kept their own copy of the same values, and nothing compared
 * them. The zones now come straight off `CITY_RECORDS` in
 * `@loci/city-editions`, the one place a city's facts live, so the web app and
 * the API cannot disagree about what hour it is in a city. What stays local is
 * only the fallback policy for codes outside the registry.
 *
 * NB: WEB-389 spec'd `CITY_EDITIONS[cityCode].timezone` and the implementation
 * declined it in favour of the local map. API-1704 revisited that call — the
 * zone lives on the shared *record* rather than on `CityEdition`, which keeps
 * CHI/MIA/NYC clocks correct without pretending those markets are registered,
 * routable editions.
 *
 * The record is read directly rather than projected into a module-level map:
 * this module is pulled into client bundles, and the lookup is the whole job.
 */
import { CITY_RECORDS, type CityCode } from '@loci/city-editions';

/**
 * Fallback zone for unrecognized city codes. UTC never throws in `Intl`.
 *
 * Deliberately NOT the API's fallback (`DEFAULT_CITY_TIME_ZONE`, Eastern):
 * this value is handed straight to `Intl` / `date-fns-tz` in render paths,
 * where UTC is the only zone guaranteed never to throw. That divergence is
 * per-surface policy for unregistered codes, not a second registry.
 */
export const DEFAULT_CITY_TIMEZONE = 'UTC';

/**
 * Resolves the IANA timezone for a city code. Returns `'UTC'` for any code not
 * in the registry so callers can pass the result straight to `Intl` /
 * `date-fns-tz` without a null check.
 *
 * The lookup is an own-property check rather than `CITY_RECORDS[code]?.timeZone`.
 * The parameter is typed `CityCode`, but callers reach it through a cast
 * (`CityHomeTemplate` passes `data.cityCode as CityCode`, a value that came off
 * the wire), and a plain object's `constructor` / `toString` resolve to
 * functions — so an optional-chain fallback would not fire and the `Object`
 * constructor would reach `Intl.DateTimeFormat({ timeZone })`, which throws.
 * That is precisely the "never throws" guarantee this resolver exists for. The
 * API-side resolver avoids the same trap by upper-casing before its lookup.
 */
export function getCityTimezone(cityCode: CityCode): string {
  return Object.prototype.hasOwnProperty.call(CITY_RECORDS, cityCode)
    ? CITY_RECORDS[cityCode].timeZone
    : DEFAULT_CITY_TIMEZONE;
}
